import React, { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { startOfWeek, addDays, format, subDays, subWeeks } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { utilisationApi } from '@/services/utilisationApi';
import type { UtilisationSummary, UtilisationMetrics, CapacitySummary, ProjectUtilisation, UtilisationTrend } from '@/types/utilisation';
import { 
  Users, 
  Clock, 
  CheckSquare, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  PoundSterling,
  Target,
  Calendar,
  Award,
  Filter,
  ArrowUpDown,
  Bell,
  X,
  CheckCircle2,
  XCircle,
  Eye,
  MessageSquare,
  MoreVertical,
  Search,
  Download,
  FileText,
  BarChart3,
  Sparkles,
  PieChart,
  Activity,
  Zap,
  Loader2,
  UserCog,
  FileWarning
} from 'lucide-react';
import { PersonaBadgeToggle } from './PersonaBadgeToggle';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { PageHeader } from '@/components/blocks/page-header';
import { useTimesheetForScreen, useTimesheet } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useManagerDashboardSummary } from '@/hooks/useManagerDashboardSummary';
import { useOrganisationSettings } from '@/hooks/useOrganisationSettings';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';

interface ManagerDashboardProps {
  onNavigate?: (path: string) => void;
}

export const ManagerDashboard: React.FC<ManagerDashboardProps> = ({ onNavigate }) => {
  const { timesheets, employees, projects, currentEmployee, updateTimesheet } = useTimesheetForScreen('dashboard');
  const { loadDirectReportTimesheets, teamTimesheetsLoading } = useTimesheet();
  const { getScreenMode } = useDataSource();
  const { summary: managerSummary, mode } = useManagerDashboardSummary('homepage');
  const { settings: orgSettings } = useOrganisationSettings('dashboard');
  
  // Get expected hours from organization settings (live mode fetches from API)
  const expectedWeeklyHours = orgSettings?.workingProfile?.standardHoursPerWeek ?? 40;
  
  // Utilisation data for Key Metrics - uses same mode as utilisation report screen
  const { isLive: isUtilisationLive } = useScreenDataMode('reports-utilisation');
  const [utilSummary, setUtilSummary] = useState<UtilisationSummary | null>(null);
  const [utilEmployeeMetrics, setUtilEmployeeMetrics] = useState<UtilisationMetrics[]>([]);
  const [utilLoading, setUtilLoading] = useState(false);

  // Capacity Planning data - calculated from utilisation metrics (weekly basis)
  const [capacityData, setCapacityData] = useState<CapacitySummary | null>(null);

  // Project Allocation data - from utilisation report
  const [projectUtilData, setProjectUtilData] = useState<ProjectUtilisation[]>([]);

  // Utilisation Trend data - last 8 weeks from utilisation report
  const [utilisationTrends, setUtilisationTrends] = useState<UtilisationTrend[]>([]);

  // Phase 2: Interactive State Management
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [teamSortBy, setTeamSortBy] = useState<'name' | 'hours' | 'utilization'>('name');
  const [teamFilterStatus, setTeamFilterStatus] = useState<'all' | 'pending' | 'active'>('all');
  const [activityFilter, setActivityFilter] = useState<'all' | 'submitted' | 'approved' | 'overdue'>('all');
  const [selectedMembers, setSelectedMembers] = useState<Set<number>>(new Set());
  const [alerts, setAlerts] = useState([
    { id: '1', type: 'overdue', message: '3 timesheets overdue for approval', count: 3, dismissible: true },
    { id: '2', type: 'utilization', message: '2 team members below 60% utilisation', count: 2, dismissible: true },
  ]);

  // Bulk Approve state
  const [showBulkApproveConfirm, setShowBulkApproveConfirm] = useState(false);
  const [isBulkApproving, setIsBulkApproving] = useState(false);

  // Measure headers to equalize bottom spacing while keeping fixed scroll heights
  const teamHeaderRef = useRef<HTMLDivElement | null>(null);
  const activityHeaderRef = useRef<HTMLDivElement | null>(null);
  const [activityExtraBottomPadding, setActivityExtraBottomPadding] = useState(0);
  const teamListRef = useRef<HTMLDivElement | null>(null);
  const activityListRef = useRef<HTMLDivElement | null>(null);
  const [activityListHeight, setActivityListHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const teamH = teamHeaderRef.current?.offsetHeight ?? 0;
    const actH = activityHeaderRef.current?.offsetHeight ?? 0;
    const diff = Math.max(0, teamH - actH);
    setActivityExtraBottomPadding(diff + 8); // small buffer so last row isnt clipped

    const leftHeight = teamListRef.current?.clientHeight ?? 500;
    setActivityListHeight(leftHeight + diff);
  }, [teamSearchQuery, teamSortBy, teamFilterStatus, activityFilter]);

  useEffect(() => {
    const recalc = () => {
      const teamH = teamHeaderRef.current?.offsetHeight ?? 0;
      const actH = activityHeaderRef.current?.offsetHeight ?? 0;
      const diff = Math.max(0, teamH - actH);
      setActivityExtraBottomPadding(diff + 8);
      const leftHeight = teamListRef.current?.clientHeight ?? 500;
      setActivityListHeight(leftHeight + diff);
    };
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, []);

  // Load utilisation data from API when in live mode (for Key Metrics cards)
  // Uses same API endpoint and date range as utilisation report (last 30 days)
  // This ensures dashboard values match the utilisation report exactly
  useEffect(() => {
    if (!isUtilisationLive) {
      setUtilSummary(null);
      return;
    }

    const loadUtilisationData = async () => {
      try {
        setUtilLoading(true);
        const today = new Date();
        const dateFrom = subDays(today, 30);
        const dateTo = today;
        
        // Use getUtilisationReport() instead of getUtilisationSummary() to match
        // the exact same data source as the Utilisation Report screen
        const report = await utilisationApi.getUtilisationReport({
          dateFrom,
          dateTo,
          includeNonBillable: true,
          groupBy: 'employee',
        });
        setUtilSummary(report.summary);
        setUtilEmployeeMetrics(report.employeeMetrics || []);
        setProjectUtilData(report.projectUtilisation || []);
        setUtilisationTrends(report.trends || []);
      } catch (error) {
        console.error('Failed to load utilisation data for dashboard:', error);
        // On error, fall back to calculated values (utilSummary stays null)
      } finally {
        setUtilLoading(false);
      }
    };

    loadUtilisationData();
  }, [isUtilisationLive]);

  // Listen for company-changed event to refetch data when switching companies
  // This ensures data is fetched from the correct integration source (local DB vs external API)
  useEffect(() => {
    const handleCompanyChange = async () => {
      if (!isUtilisationLive) return;

      try {
        setUtilLoading(true);
        const today = new Date();
        const dateFrom = subDays(today, 30);
        const dateTo = today;
        
        const report = await utilisationApi.getUtilisationReport({
          dateFrom,
          dateTo,
          includeNonBillable: true,
          groupBy: 'employee',
        });
        setUtilSummary(report.summary);
        setUtilEmployeeMetrics(report.employeeMetrics || []);
        setProjectUtilData(report.projectUtilisation || []);
        setUtilisationTrends(report.trends || []);
      } catch (error) {
        console.error('Failed to load utilisation data after company change:', error);
      } finally {
        setUtilLoading(false);
      }
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => window.removeEventListener('company-changed', handleCompanyChange);
  }, [isUtilisationLive]);

  // Lazy load direct reports' timesheets when the manager homepage is opened in live mode
  // This optimization moves the team timesheet fetching from app initialization to when actually needed
  useEffect(() => {
    if (mode === 'live') {
      loadDirectReportTimesheets();
    }
  }, [mode, loadDirectReportTimesheets]);

  // Update alerts with live data when in live mode
  useEffect(() => {
    if (mode !== 'live') return;

    setAlerts(prevAlerts => prevAlerts.map(alert => {
      if (alert.type === 'overdue' && managerSummary) {
        const count = managerSummary.approvalsRequired.overdueCount;
        return {
          ...alert,
          count,
          message: `${count} timesheet${count === 1 ? '' : 's'} overdue for approval`,
        };
      }

      if (alert.type === 'utilization' && utilEmployeeMetrics.length > 0) {
        const count = utilEmployeeMetrics.filter(emp => emp.utilisationRate < 0.6).length;
        return {
          ...alert,
          count,
          message: `${count} team member${count === 1 ? '' : 's'} below 60% utilisation`,
        };
      }

      return alert;
    }));
  }, [mode, managerSummary, utilEmployeeMetrics]);

  // Calculate capacity planning data from utilisation metrics (weekly basis)
  // This runs when utilisation data is loaded in live mode
  useEffect(() => {
    if (!isUtilisationLive || utilEmployeeMetrics.length === 0) {
      setCapacityData(null);
      return;
    }

    // Calculate capacity metrics from employee utilisation data
    // Use organization settings for expected hours (live mode fetches from API)
    const WEEKLY_CAPACITY_PER_EMPLOYEE = expectedWeeklyHours;
    const employeeCount = utilEmployeeMetrics.length;
    const totalCapacity = employeeCount * WEEKLY_CAPACITY_PER_EMPLOYEE;
    
    // Calculate total allocated hours (average weekly from the utilisation data)
    // The utilisation data is for 30 days, so divide by ~4.3 weeks to get weekly average
    const WEEKS_IN_PERIOD = 4.3;
    const totalAllocatedMonthly = utilEmployeeMetrics.reduce((sum, emp) => sum + emp.totalHours, 0);
    const totalAllocated = Math.round(totalAllocatedMonthly / WEEKS_IN_PERIOD);
    
    // Calculate available capacity
    const totalAvailable = Math.max(0, totalCapacity - totalAllocated);
    
    // Count employees by utilisation status
    // Over-allocated: > 100% utilisation (working more than capacity)
    // Under-utilised: < 60% utilisation
    // Optimal: 60-100% utilisation
    let overAllocatedCount = 0;
    let underUtilisedCount = 0;
    let optimalCount = 0;
    
    utilEmployeeMetrics.forEach(emp => {
      const rate = emp.utilisationRate;
      if (rate > 1.0) {
        overAllocatedCount++;
      } else if (rate < 0.6) {
        underUtilisedCount++;
      } else {
        optimalCount++;
      }
    });
    
    // Calculate average utilisation
    const averageUtilisation = utilEmployeeMetrics.reduce((sum, emp) => sum + emp.utilisationRate, 0) / employeeCount;
    
    setCapacityData({
      totalCapacity,
      totalAllocated,
      totalAvailable,
      overAllocatedCount,
      underUtilisedCount,
      optimalCount,
      averageUtilisation,
      recommendations: [], // Recommendations could be added later
    });
  }, [isUtilisationLive, utilEmployeeMetrics, expectedWeeklyHours]);

  // Calculate team metrics - Approvals uses direct reports filter, Team This Week uses current week filter
  const teamMetrics = useMemo(() => {
    // Get direct reports for approvals
    const directReports = employees.filter(e => e.managerId === currentEmployee.id);
    const directReportIds = new Set(directReports.map(e => e.id));
    
    // Filter timesheets to only include direct reports
    const directReportTimesheets = timesheets.filter(ts => directReportIds.has(ts.employeeId));
    
    // Calculate pending approvals - same logic as Approvals screen (direct reports)
    const myPendingTimesheets = directReportTimesheets.filter(ts => ts.status === 'submitted');
    const myPendingCount = myPendingTimesheets.length;
    const orgWidePendingCount = timesheets.filter(ts => ts.status === 'submitted').length;
    
    // Calculate overdue approvals from direct reports' timesheets
    const overdueApprovalsCount = myPendingTimesheets.filter(ts => {
      const daysSinceSubmission = ts.submittedAt 
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      return daysSinceSubmission > 3;
    }).length;

    // Calculate missing timesheets - employees who haven't submitted timesheets for recent weeks
    // Check last 4 weeks for missing submissions
    const now = new Date();
    const fourWeeksAgo = subWeeks(now, 4);
    
    // Get all week endings in the last 4 weeks
    const recentWeekEndings: string[] = [];
    for (let i = 0; i < 4; i++) {
      const weekStart = startOfWeek(subWeeks(now, i), { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);
      recentWeekEndings.push(format(weekEnd, 'yyyy-MM-dd'));
    }
    
    // Count missing timesheets for direct reports
    let missingTimesheetsCount = 0;
    let overdueSubmissionsCount = 0;
    
    directReports.forEach(employee => {
      recentWeekEndings.forEach(weekEnding => {
        const hasTimesheet = directReportTimesheets.some(ts => 
          format(new Date(ts.weekEnding), 'yyyy-MM-dd') === weekEnding && 
          ts.employeeId === employee.id
        );
        
        // If no timesheet exists for this week ending, it's missing
        if (!hasTimesheet) {
          const weekEndDate = new Date(weekEnding);
          const daysSinceWeekEnding = Math.floor((now.getTime() - weekEndDate.getTime()) / (1000 * 60 * 60 * 24));
          
          // Only count as missing if week has ended and grace period (7 days) has passed
          if (daysSinceWeekEnding > 7) {
            missingTimesheetsCount++;
            
            // Count as overdue if more than 7 days past week ending
            if (daysSinceWeekEnding > 7) {
              overdueSubmissionsCount++;
            }
          }
        }
      });
    });
    
    // Also count draft timesheets that are overdue (more than 7 days past week ending)
    directReportTimesheets.forEach(ts => {
      if (ts.status === 'draft') {
        const weekEndDate = new Date(ts.weekEnding);
        const daysSinceWeekEnding = Math.floor((now.getTime() - weekEndDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysSinceWeekEnding > 7) {
          overdueSubmissionsCount++;
        }
      }
    });

    // Calculate Team This Week - use same week calculation as ThisWeekCard
    // Week starts on Monday (weekStartsOn: 1), ends on Sunday (Monday + 6 days)
    const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const currentWeekEnd = addDays(currentWeekStart, 6);
    
    // Filter timesheets for current week only - exclude draft and rejected
    // Only count submitted, approved, or locked timesheets
    const finalizedStatuses = ['submitted', 'approved', 'locked'];
    const currentWeekTimesheets = directReportTimesheets.filter(ts => {
      const tsWeekEnding = format(new Date(ts.weekEnding), 'yyyy-MM-dd');
      const currentWeekEndStr = format(currentWeekEnd, 'yyyy-MM-dd');
      const isCurrentWeek = tsWeekEnding === currentWeekEndStr;
      const isFinalizedStatus = finalizedStatuses.includes(ts.status);
      return isCurrentWeek && isFinalizedStatus;
    });
    
    const totalHours = currentWeekTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        return entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0);
      }, 0);
    }, 0);

    const billableHours = currentWeekTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        const project = projects.find(p => p.id === entry.projectId);
        const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
        return entrySum + (project?.billable ? hours : 0);
      }, 0);
    }, 0);

    const utilizationRate = totalHours > 0 ? (billableHours / totalHours) * 100 : 0;
    
        // Team size shows all active employees
        const activeEmployees = employees.filter(e => !e.status || e.status === 'active');

    // Use API data for approvals when in live mode (backend knows the correct approver assignments)
    const approvalsFromApi = mode === 'live' && managerSummary?.approvalsRequired;

    // Use API data for team this week when in live mode (server-side current week calculation)
    const teamThisWeekFromApi = mode === 'live' && managerSummary?.teamThisWeek;

    // Use API data for team performance when in live mode
    const teamPerformanceFromApi = mode === 'live' && managerSummary?.teamPerformance;
    
    // Get utilization value - from API in live mode, otherwise calculated
    const teamPerformanceUtilization = teamPerformanceFromApi 
      ? Math.round(Number(managerSummary.teamPerformance.utilizationRate))
      : Math.round(utilizationRate);
    
    // Get target rate from org settings (primary), fallback to API, then 80%
    // This ensures consistency between displayed target and isAboveTarget calculation
    const teamPerformanceTarget = orgSettings?.utilisation?.targetUtilisationRate != null
      ? Math.round(orgSettings.utilisation.targetUtilisationRate * 100)
      : teamPerformanceFromApi && managerSummary.teamPerformance.utilizationTarget != null
        ? Math.round(Number(managerSummary.teamPerformance.utilizationTarget))
        : 80;
    
    // Calculate isAboveTarget locally using the same target we display for consistency
    const teamPerformanceIsAboveTarget = teamPerformanceUtilization >= teamPerformanceTarget;
    
    return {
      pendingApprovals: approvalsFromApi ? managerSummary.approvalsRequired.pendingCount : myPendingCount,
      orgWidePendingCount,
      overdueApprovals: approvalsFromApi ? managerSummary.approvalsRequired.overdueCount : overdueApprovalsCount,
      totalHours: teamThisWeekFromApi
        ? Math.round(Number(managerSummary.teamThisWeek.totalHours))
        : Math.round(totalHours),
      billableHours: teamThisWeekFromApi
        ? Math.round(Number(managerSummary.teamThisWeek.billableHours))
        : Math.round(billableHours),
      utilizationRate: teamThisWeekFromApi
        ? Math.round(Number(managerSummary.teamThisWeek.utilizationRate))
        : Math.round(utilizationRate),
      teamSize: teamThisWeekFromApi
        ? managerSummary.teamThisWeek.teamSize
        : activeEmployees.length,
      trendPercentage: teamThisWeekFromApi
        ? Math.round(Number(managerSummary.teamThisWeek.trendPercentage))
        : 0,
      // Missing timesheets data
      missingTimesheets: missingTimesheetsCount,
      overdueSubmissions: overdueSubmissionsCount,
      // Team Performance section - use calculated values above
      teamPerformanceUtilization,
      teamPerformanceTarget,
      teamPerformanceIsAboveTarget,
      teamPerformanceTrend: teamPerformanceFromApi 
        ? Math.round(Number(managerSummary.teamPerformance.trendPercentage))
        : 0,
      teamPerformanceTeamSize: teamPerformanceFromApi 
        ? managerSummary.teamPerformance.teamSize
        : activeEmployees.length,
    };
  }, [timesheets, employees, projects, currentEmployee, mode, managerSummary, orgSettings]);

  // Helper function to get utilization accent class
  const getUtilizationAccent = (utilization: number): string => {
    if (utilization >= 85) return 'accent-success';
    if (utilization >= 75) return 'accent-info';
    if (utilization >= 60) return 'accent-warning';
    return 'accent-destructive';
  };

  // Calculate team member stats - show all employees, not just first 5
  const teamMemberStats = useMemo(() => {
    return employees.map(employee => {
      const employeeTimesheets = timesheets.filter(ts => ts.employeeId === employee.id);
      const totalHours = employeeTimesheets
        .filter(ts => ts.status === 'approved')
        .reduce((sum, ts) => {
          return sum + ts.entries.reduce((entrySum, entry) => {
            return entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0);
          }, 0);
        }, 0);

      const pendingCount = employeeTimesheets.filter(ts => ts.status === 'submitted').length;
      
      // Calculate utilization based on approved hours vs expected hours from org settings
      const EXPECTED_WEEKLY_HOURS = expectedWeeklyHours;
      const weeksOfData = Math.max(1, new Set(employeeTimesheets.map(ts => ts.weekEnding.toString())).size);
      const avgWeeklyHours = totalHours / weeksOfData;
      const utilization = Math.min(100, Math.round((avgWeeklyHours / EXPECTED_WEEKLY_HOURS) * 100));

      return {
        employee,
        totalHours: Math.round(totalHours),
        pendingCount,
        utilization,
      };
    });
  }, [employees, timesheets, expectedWeeklyHours]);

  // Filter and sort team member stats based on user selections
  const filteredTeamMemberStats = useMemo(() => {
    let stats = [...teamMemberStats];

    // Apply search filter
    if (teamSearchQuery.trim()) {
      const query = teamSearchQuery.toLowerCase().trim();
      stats = stats.filter(({ employee }) => 
        employee.name.toLowerCase().includes(query) ||
        employee.department.toLowerCase().includes(query) ||
        employee.email.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (teamFilterStatus === 'pending') {
      stats = stats.filter(({ pendingCount }) => pendingCount > 0);
    } else if (teamFilterStatus === 'active') {
      stats = stats.filter(({ employee }) => 
        !employee.status || employee.status.toLowerCase() === 'active'
      );
    }

    // Apply sorting
    switch (teamSortBy) {
      case 'name':
        stats.sort((a, b) => a.employee.name.localeCompare(b.employee.name));
        break;
      case 'hours':
        stats.sort((a, b) => b.totalHours - a.totalHours);
        break;
      case 'utilization':
        stats.sort((a, b) => b.utilization - a.utilization);
        break;
    }

    return stats;
  }, [teamMemberStats, teamSearchQuery, teamFilterStatus, teamSortBy]);

  // Calculate timesheets to approve for selected members
  const timesheetsToApprove = useMemo(() => {
    if (selectedMembers.size === 0) return [];
    return timesheets.filter(ts => 
      ts.status === 'submitted' && 
      selectedMembers.has(ts.employeeId)
    );
  }, [timesheets, selectedMembers]);

  // Handle bulk approve click - show confirmation dialog
  const handleBulkApproveClick = () => {
    if (timesheetsToApprove.length === 0) {
      toast.info('No pending timesheets for selected team members');
      return;
    }
    setShowBulkApproveConfirm(true);
  };

  // Execute bulk approve - actually approve the timesheets
  const executeBulkApprove = () => {
    setIsBulkApproving(true);
    setShowBulkApproveConfirm(false);

    const count = timesheetsToApprove.length;
    
    if (count > 10) {
      toast.loading(`Approving ${count} timesheets...`, { id: 'bulk-approve' });
    }

    // Approve each timesheet
    timesheetsToApprove.forEach(ts => {
      updateTimesheet({
        ...ts,
        status: 'approved' as const,
        approvedAt: new Date(),
        approverId: currentEmployee.id,
        approverName: currentEmployee.name
      });
    });

    // Clear selection and show success
    setSelectedMembers(new Set());
    setIsBulkApproving(false);
    
    if (count > 10) {
      toast.dismiss('bulk-approve');
    }
    toast.success(`Successfully approved ${count} timesheet${count !== 1 ? 's' : ''}`);
  };

  // Export helper function to trigger file download
  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export Team Summary as PDF report
  const exportTeamSummary = () => {
    const reportDate = format(new Date(), 'yyyy-MM-dd HH:mm');
    const weekEnd = format(addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 6), 'yyyy-MM-dd');
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4',
    });

    const margin = 40;
    const lineHeight = 16;
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = margin;

    const addLine = (text: string, fontSize = 10, isBold = false) => {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.text(text, margin, y);
      y += lineHeight;
    };

    const addBlankLine = () => {
      y += lineHeight / 2;
    };

    // Title
    addLine('TEAM SUMMARY REPORT', 18, true);
    addBlankLine();
    
    // Report info
    addLine(`Generated: ${reportDate}`);
    addLine(`Manager: ${currentEmployee.name}`);
    addLine(`Week Ending: ${weekEnd}`);
    addBlankLine();
    
    // Overview section
    addLine('OVERVIEW', 14, true);
    addLine(`Team Size: ${teamMetrics.teamSize}`);
    addLine(`Pending Approvals: ${teamMetrics.pendingApprovals}`);
    addLine(`Overdue Approvals: ${teamMetrics.overdueApprovals}`);
    addLine(`Total Hours (This Week): ${teamMetrics.totalHours}h`);
    addLine(`Billable Hours: ${teamMetrics.billableHours}h`);
    addLine(`Utilisation Rate: ${teamMetrics.utilizationRate}%`);
    addBlankLine();
    
    // Team Performance section
    addLine('TEAM PERFORMANCE', 14, true);
    addLine(`Current Utilisation: ${teamMetrics.teamPerformanceUtilization}%`);
    addLine(`Target: ${teamMetrics.teamPerformanceTarget}%`);
    addLine(`Status: ${teamMetrics.teamPerformanceIsAboveTarget ? 'Above Target' : 'Below Target'}`);
    addLine(`Trend: ${teamMetrics.teamPerformanceTrend >= 0 ? '+' : ''}${teamMetrics.teamPerformanceTrend}%`);
    addBlankLine();
    
    // Team Members section
    addLine('TEAM MEMBERS', 14, true);
    addBlankLine();
    
    teamMemberStats.forEach(({ employee, totalHours, pendingCount, utilization }) => {
      addLine(employee.name, 11, true);
      addLine(`  Department: ${employee.department}`);
      addLine(`  Total Hours: ${totalHours}h`);
      addLine(`  Utilisation: ${utilization}%`);
      addLine(`  Pending Timesheets: ${pendingCount}`);
      addBlankLine();
    });

    doc.save(`team-summary-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('Team Summary PDF downloaded');
  };

  // Export Utilisation Data as CSV
  const exportUtilizationData = () => {
    const headers = ['Employee Name', 'Department', 'Email', 'Total Hours', 'Utilisation %', 'Pending Timesheets', 'Status'];
    const rows = teamMemberStats.map(({ employee, totalHours, pendingCount, utilization }) => [
      employee.name,
      employee.department,
      employee.email,
      totalHours.toString(),
      utilization.toString(),
      pendingCount.toString(),
      employee.status || 'active'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    downloadFile(csvContent, `utilization-data-${format(new Date(), 'yyyy-MM-dd')}.csv`, 'text/csv');
    toast.success('Utilisation Data exported to CSV');
  };

  // Export Project Hours as CSV
  const exportProjectHours = () => {
    const projectHoursMap = new Map<string, { projectName: string; projectCode: string; clientName: string; totalHours: number; billable: boolean }>();
    
    timesheets.forEach(ts => {
      ts.entries.forEach(entry => {
        const project = projects.find(p => p.id === entry.projectId);
        if (project) {
          const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
          const existing = projectHoursMap.get(project.id);
          if (existing) {
            existing.totalHours += hours;
          } else {
            projectHoursMap.set(project.id, {
              projectName: project.name,
              projectCode: project.code,
              clientName: project.clientName,
              totalHours: hours,
              billable: project.billable
            });
          }
        }
      });
    });
    
    const headers = ['Project Code', 'Project Name', 'Client', 'Total Hours', 'Billable'];
    const rows = Array.from(projectHoursMap.values())
      .sort((a, b) => b.totalHours - a.totalHours)
      .map(p => [
        p.projectCode,
        p.projectName,
        p.clientName,
        p.totalHours.toFixed(1),
        p.billable ? 'Yes' : 'No'
      ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    downloadFile(csvContent, `project-hours-${format(new Date(), 'yyyy-MM-dd')}.csv`, 'text/csv');
    toast.success('Project Hours exported to CSV');
  };

  return (
    <div className="space-y-6">
      {/* Header with integrated persona toggle */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="Manager Homepage"
          subtitle="Team oversight, approvals, and performance metrics"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<UserCog />}
        />
        <FeatureFeedbackCollector feature="Manager Homepage" />
        <div className="flex items-center gap-2 flex-shrink-0">
          <PersonaBadgeToggle />
        </div>
      </div>

      {/* ========================================
          KEY METRICS - Quick Stats
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Key Metrics
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Card 1: Pending Approvals - Dynamic variant based on status */}
        <NavigationMetricCardAlt
          title="Pending Approvals"
          value={teamMetrics.pendingApprovals.toString()}
          icon={CheckSquare}
          variant={
            teamMetrics.pendingApprovals === 0 ? "success" :
            teamMetrics.overdueApprovals > 0 ? "error" :
            "warning"
          }
          change={
            teamMetrics.overdueApprovals > 0 
              ? `${teamMetrics.overdueApprovals} overdue`
              : teamMetrics.pendingApprovals === 0
              ? "All up to date"
              : "Awaiting review"
          }
          onClick={() => onNavigate?.('/approvals')}
        />

        {/* Card 2: Missing Timesheets - Dynamic variant based on status */}
        <NavigationMetricCardAlt
          title="Missing Timesheets"
          value={teamMetrics.missingTimesheets.toString()}
          icon={FileWarning}
          variant={
            teamMetrics.missingTimesheets === 0 ? "success" :
            teamMetrics.overdueSubmissions > 5 ? "error" :
            "warning"
          }
          change={
            teamMetrics.overdueSubmissions > 0 
              ? `${teamMetrics.overdueSubmissions} overdue submissions`
              : "All up to date"
          }
          onClick={() => onNavigate?.('/reports/missing-timesheets')}
        />

        {/* Card 3: Total Hours - Always info */}
        <NavigationMetricCardAlt
          title="Total Hours"
          value={
            isUtilisationLive && utilSummary 
              ? utilSummary.totalHours.toFixed(1) 
              : teamMetrics.totalHours.toString()
          }
          icon={Clock}
          variant="info"
          change={
            isUtilisationLive && utilSummary 
              ? 'Last 30 days' 
              : 'This week'
          }
        />

        {/* Card 4: Utilization Rate - Dynamic variant based on target */}
        <NavigationMetricCardAlt
          title="Utilisation Rate"
          value={`${
            isUtilisationLive && utilSummary 
              ? (utilSummary.averageUtilisation * 100).toFixed(1) 
              : teamMetrics.utilizationRate
          }%`}
          icon={Target}
          variant={(() => {
            const rate = isUtilisationLive && utilSummary 
              ? utilSummary.averageUtilisation * 100 
              : teamMetrics.utilizationRate;
            const targetRate = orgSettings?.utilisation?.targetUtilisationRate != null
              ? Math.round(orgSettings.utilisation.targetUtilisationRate * 100)
              : 80;
            // Success if at or above target, warning if within 10% below, error if more than 10% below
            return rate >= targetRate ? "success" :
                   rate >= (targetRate - 10) ? "warning" :
                   "error";
          })()}
          change={(() => {
            const rate = isUtilisationLive && utilSummary 
              ? utilSummary.averageUtilisation * 100 
              : teamMetrics.utilizationRate;
            const targetRate = orgSettings?.utilisation?.targetUtilisationRate != null
              ? Math.round(orgSettings.utilisation.targetUtilisationRate * 100)
              : 80;
            return rate >= targetRate 
              ? `Above target (${targetRate}%)`
              : `Below target (${targetRate}%)`;
          })()}
        />
      </div>
      </div>

      {/* ========================================
          TIER 3: QUICK ACTIONS - Manager Tools
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Quick Actions
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onNavigate?.('/approvals')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckSquare className="h-4 w-4 text-primary" />
              Review Approvals
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-muted-foreground">
              {teamMetrics.pendingApprovals} timesheets waiting for your review
            </p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onNavigate?.('/team/members')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Team Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-muted-foreground">
              View team member performance and schedules
            </p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onNavigate?.('/reports/utilisation')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Utilisation Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-muted-foreground">
              Analyse team utilisation and productivity trends
            </p>
          </CardContent>
        </Card>
      </div>
      </div>

      {/* ========================================
          PHASE 2: ALERTS & NOTIFICATIONS PANEL
          ======================================== */}
      {alerts.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-border" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Alerts & Notifications
            </h2>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="grid gap-3">
            {alerts.map((alert) => (
              <Card key={alert.id} className="bg-burntorange-50/50">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-2 rounded-lg bg-burntorange-100">
                        <AlertTriangle className="h-4 w-4 text-burntorange-600" />
                      </div>
                      <p className="text-sm font-medium text-burntorange-900 flex-1">{alert.message}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs"
                        onClick={() => onNavigate?.('/approvals')}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Review
                      </Button>
                      {alert.dismissible && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs"
                          onClick={() => setAlerts(alerts.filter(a => a.id !== alert.id))}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Dismiss
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ========================================
          PHASE 3: INTELLIGENCE & ANALYTICS
          ======================================== */}
      
      {/* AI-Powered Team Insights */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            AI-Powered Insights
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Predictive Analytics Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="h-5 w-5 text-electricviolet-500" />
                  Predictive Analytics
                </CardTitle>
                <Badge variant="secondary" className="bg-electricviolet-100 text-electricviolet-700">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-success-50 border border-success-200">
                  <TrendingUp className="h-4 w-4 text-success-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-success-900">Positive Trend Detected</p>
                    <p className="text-xs text-success-700 mt-1">
                      Team utilisation expected to reach 85% by next week based on current trajectory
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-info-50 border border-info-200">
                  <Activity className="h-4 w-4 text-info-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-info-900">Resource Optimization</p>
                    <p className="text-xs text-info-700 mt-1">
                      3 team members have capacity for additional projects (avg 8h/week available)
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-burntorange-50 border border-burntorange-200">
                  <AlertTriangle className="h-4 w-4 text-burntorange-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-burntorange-900">Potential Bottleneck</p>
                    <p className="text-xs text-burntorange-700 mt-1">
                      Sarah Johnson at 95% capacity - consider redistributing workload
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Capacity Planning Widget */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-info-500" />
                  Capacity Planning
                </CardTitle>
                <Button size="sm" variant="outline" className="h-7 text-xs">
                  <Download className="h-3 w-3 mr-1" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {utilLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-info-500" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading capacity data...</span>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <div className="accent-success">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Available Capacity</span>
                        <span className="font-semibold text-[var(--accent-text)]">
                          {capacityData ? `${capacityData.totalAvailable}h/week` : '120h/week'}
                        </span>
                      </div>
                      <Progress 
                        value={capacityData ? (capacityData.totalAvailable / capacityData.totalCapacity) * 100 : 30} 
                        className="h-2" 
                        indicatorClassName="bg-[var(--accent-bg-solid)]" 
                      />
                    </div>
                    
                    <div className="accent-info">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Allocated Hours</span>
                        <span className="font-semibold text-[var(--accent-text)]">
                          {capacityData ? `${capacityData.totalAllocated}h/week` : '280h/week'}
                        </span>
                      </div>
                      <Progress 
                        value={capacityData ? (capacityData.totalAllocated / capacityData.totalCapacity) * 100 : 70} 
                        className="h-2" 
                        indicatorClassName="bg-[var(--accent-bg-solid)]" 
                      />
                    </div>
                    
                    <div className="accent-orange">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Overallocated</span>
                        <span className="font-semibold text-[var(--accent-text)]">
                          {capacityData ? `${capacityData.overAllocatedCount} employees` : '0h/week'}
                        </span>
                      </div>
                      <Progress 
                        value={capacityData && capacityData.totalCapacity > 0 
                          ? (capacityData.overAllocatedCount / utilEmployeeMetrics.length) * 100 
                          : 0} 
                        className="h-2" 
                        indicatorClassName="bg-[var(--accent-bg-solid)]" 
                      />
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total Team Capacity</span>
                      <span className="font-bold">
                        {capacityData ? `${capacityData.totalCapacity}h/week` : '400h/week'}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Historical Trends & Project Allocation */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics & Trends
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Utilization Trend Chart */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Utilisation Trend</CardTitle>
                  <CardDescription>Last 8 weeks performance</CardDescription>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onNavigate?.('/reports/utilisation')}>
                  <FileText className="h-3 w-3 mr-1" />
                  Details
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(() => {
                  // Calculate date ranges for the last 8 weeks
                  const getWeekRanges = () => {
                    const ranges: string[] = [];
                    const today = new Date();
                    
                    // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
                    const getOrdinalSuffix = (day: number) => {
                      if (day > 3 && day < 21) return 'th';
                      switch (day % 10) {
                        case 1: return 'st';
                        case 2: return 'nd';
                        case 3: return 'rd';
                        default: return 'th';
                      }
                    };
                    
                    for (let i = 7; i >= 0; i--) {
                      const weekEnd = subWeeks(today, i);
                      const weekStart = subWeeks(weekEnd, 1);
                      const startDay = weekStart.getDate();
                      const endDay = weekEnd.getDate();
                      ranges.push(`${startDay}${getOrdinalSuffix(startDay)} ${format(weekStart, 'MMM')} - ${endDay}${getOrdinalSuffix(endDay)} ${format(weekEnd, 'MMM')}`);
                    }
                    return ranges;
                  };
                  const weekRanges = getWeekRanges();

                  return [
                    { week: 'Week 1', dateRange: weekRanges[0], value: utilisationTrends.length >= 1 ? Math.round(utilisationTrends.slice(-8)[0]?.utilisationRate * 100) : 72, color: 'bg-error-500' },
                    { week: 'Week 2', dateRange: weekRanges[1], value: utilisationTrends.length >= 2 ? Math.round(utilisationTrends.slice(-8)[1]?.utilisationRate * 100) : 75, color: 'bg-burntorange-500' },
                    { week: 'Week 3', dateRange: weekRanges[2], value: utilisationTrends.length >= 3 ? Math.round(utilisationTrends.slice(-8)[2]?.utilisationRate * 100) : 78, color: 'bg-burntorange-500' },
                    { week: 'Week 4', dateRange: weekRanges[3], value: utilisationTrends.length >= 4 ? Math.round(utilisationTrends.slice(-8)[3]?.utilisationRate * 100) : 82, color: 'bg-success-500' },
                    { week: 'Week 5', dateRange: weekRanges[4], value: utilisationTrends.length >= 5 ? Math.round(utilisationTrends.slice(-8)[4]?.utilisationRate * 100) : 85, color: 'bg-success-500' },
                    { week: 'Week 6', dateRange: weekRanges[5], value: utilisationTrends.length >= 6 ? Math.round(utilisationTrends.slice(-8)[5]?.utilisationRate * 100) : 83, color: 'bg-success-500' },
                    { week: 'Week 7', dateRange: weekRanges[6], value: utilisationTrends.length >= 7 ? Math.round(utilisationTrends.slice(-8)[6]?.utilisationRate * 100) : 81, color: 'bg-success-500' },
                    { week: 'Week 8', dateRange: weekRanges[7], value: utilisationTrends.length >= 8 ? Math.round(utilisationTrends.slice(-8)[7]?.utilisationRate * 100) : 84, color: 'bg-success-500' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-48"><span className="font-bold">{item.week}:</span> {item.dateRange}</span>
                      <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden">
                        <div 
                          className={`h-full ${item.color} flex items-center justify-end pr-2 transition-all duration-500`}
                          style={{ width: `${item.value}%` }}
                        >
                          <span className="text-xs font-bold text-white">{item.value}%</span>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
              <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Average</span>
                <span className="font-semibold text-success-600">
                  {(() => {
                    if (utilisationTrends.length >= 2) {
                      const recent = utilisationTrends.slice(-8);
                      const avg = Math.round(recent.reduce((sum, t) => sum + t.utilisationRate * 100, 0) / recent.length);
                      const oldestRate = Math.round(recent[0].utilisationRate * 100);
                      const newestRate = Math.round(recent[recent.length - 1].utilisationRate * 100);
                      const diff = newestRate - oldestRate;
                      return `${avg}% ${diff >= 0 ? '↑' : '↓'} ${Math.abs(diff)}%`;
                    }
                    return '80% ↑ 8%';
                  })()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Project Allocation Breakdown */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Project Allocation</CardTitle>
                  <CardDescription>Current team distribution</CardDescription>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onNavigate?.('/projects/active')}>
                  <PieChart className="h-3 w-3 mr-1" />
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {utilLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-info-500" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading project data...</span>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {(() => {
                      const demoData = [
                        { project: 'Website Redesign', hours: 120, percentage: 43 },
                        { project: 'Mobile App Dev', hours: 80, percentage: 29 },
                        { project: 'API Integration', hours: 50, percentage: 18 },
                        { project: 'Internal Tools', hours: 30, percentage: 10 },
                      ];
                      
                      // Use live data if available, otherwise use demo data
                      const projectData = projectUtilData.length > 0
                        ? projectUtilData
                            .sort((a, b) => b.totalHours - a.totalHours)
                            .slice(0, 4)
                            .map(p => {
                              const totalProjectHours = projectUtilData.reduce((sum, proj) => sum + proj.totalHours, 0);
                              return {
                                project: p.projectName,
                                hours: Math.round(p.totalHours),
                                percentage: totalProjectHours > 0 ? Math.round((p.totalHours / totalProjectHours) * 100) : 0,
                              };
                            })
                        : demoData;
                      
                      return projectData.map((item, index) => {
                        return (
                          <div key={index} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{item.project}</span>
                              <div className="text-right">
                                <span className="text-sm font-semibold text-teal-600">{item.hours}h</span>
                                <span className="text-xs text-muted-foreground ml-1">({item.percentage}%)</span>
                              </div>
                            </div>
                            <Progress value={item.percentage} className="h-2 [&>div]:bg-teal-500" />
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Total Allocated</span>
                    <span className="font-bold">
                      {projectUtilData.length > 0 
                        ? `${Math.round(projectUtilData.reduce((sum, p) => sum + p.totalHours, 0))}h` 
                        : '280h/week'}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Export and Reporting */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Reports & Export
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Generate Reports</CardTitle>
            <CardDescription>Export team data and analytics for external use</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <Button variant="outline" className="justify-start h-auto py-3" onClick={exportTeamSummary}>
                <div className="flex flex-col items-start gap-1 w-full">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    <span className="font-medium">Team Summary</span>
                  </div>
                  <span className="text-xs text-muted-foreground">PDF Report</span>
                </div>
              </Button>
              
              <Button variant="outline" className="justify-start h-auto py-3" onClick={exportUtilizationData}>
                <div className="flex flex-col items-start gap-1 w-full">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    <span className="font-medium">Utilisation Data</span>
                  </div>
                  <span className="text-xs text-muted-foreground">CSV Export</span>
                </div>
              </Button>
              
              <Button variant="outline" className="justify-start h-auto py-3" onClick={exportProjectHours}>
                <div className="flex flex-col items-start gap-1 w-full">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    <span className="font-medium">Project Hours</span>
                  </div>
                  <span className="text-xs text-muted-foreground">CSV Export</span>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========================================
          TIER 4: TEAM DETAILS - Individual Performance
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Team Details
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Team Performance and Recent Team Activity side by side on large screens */}
        <div className="grid gap-4 lg:grid-cols-2">
        <Card className="flex flex-col">
        <CardHeader ref={teamHeaderRef}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle>Team Performance</CardTitle>
              <CardDescription>Individual team member metrics and status</CardDescription>
            </div>
            {selectedMembers.size > 0 && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedMembers.size} selected</Badge>
                <Button size="sm" variant="outline" onClick={() => setSelectedMembers(new Set())}>
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
                <Button 
                  size="sm" 
                  disabled={isBulkApproving}
                  onClick={handleBulkApproveClick}
                >
                  {isBulkApproving ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                  )}
                  Bulk Approve {timesheetsToApprove.length > 0 && `(${timesheetsToApprove.length})`}
                </Button>
              </div>
            )}
          </div>
          
          {/* Phase 2: Filtering and Sorting Controls */}
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search team members..."
                value={teamSearchQuery}
                onChange={(e) => setTeamSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={teamFilterStatus} onValueChange={(value: any) => setTeamFilterStatus(value)}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                <SelectItem value="pending">Has Pending</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={teamSortBy} onValueChange={(value: any) => setTeamSortBy(value)}>
              <SelectTrigger className="w-[150px]">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Sort by Name</SelectItem>
                <SelectItem value="hours">Sort by Hours</SelectItem>
                <SelectItem value="utilization">Sort by Utilisation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div ref={teamListRef} className="space-y-4 min-h-[500px] max-h-[500px] overflow-y-auto pr-1">
            {filteredTeamMemberStats.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No team members match your filters</p>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => {
                    setTeamSearchQuery('');
                    setTeamFilterStatus('all');
                  }}
                >
                  Clear filters
                </Button>
              </div>
            ) : null}
            {filteredTeamMemberStats.map(({ employee, totalHours, pendingCount, utilization }) => (
              <Card key={employee.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedMembers.has(employee.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedMembers);
                            if (e.target.checked) {
                              newSelected.add(employee.id);
                            } else {
                              newSelected.delete(employee.id);
                            }
                            setSelectedMembers(newSelected);
                          }}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-primary">
                            {employee.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{employee.name}</p>
                          <p className="text-xs text-muted-foreground">{employee.department}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {pendingCount > 0 && (
                          <Badge variant="outline" className="bg-burntorange-50 text-burntorange-700 border-burntorange-200">
                            {pendingCount} pending
                          </Badge>
                        )}
                        <div className="text-right">
                          <p className="text-sm font-medium">{utilization}%</p>
                          <p className="text-xs text-muted-foreground">Utilisation</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{totalHours}h</p>
                          <p className="text-xs text-muted-foreground">Total hours</p>
                        </div>
                        
                        {/* Phase 2: Quick Action Menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onNavigate?.(`/team/${employee.id}`)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onNavigate?.(`/approvals?employee=${employee.id}`)}>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Review Timesheets
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onNavigate?.(`/messages?to=${employee.id}`)}>
                              <MessageSquare className="h-4 w-4 mr-2" />
                              Send Message
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Team Activity - Phase 2: With Filtering */}
      <Card className="flex flex-col">
        <CardHeader ref={activityHeaderRef}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle>Recent Team Activity</CardTitle>
              <CardDescription>Latest submissions and approvals across your team</CardDescription>
            </div>
            <Select value={activityFilter} onValueChange={(value: any) => setActivityFilter(value)}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activity</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div ref={activityListRef} className="space-y-4 overflow-y-auto pr-1" style={{ height: activityListHeight, paddingBottom: activityExtraBottomPadding }} >
            {(() => {
              // Helper function to format relative time
              const getRelativeTime = (date: Date | undefined) => {
                if (!date) return 'Unknown';
                const now = new Date();
                const diffMs = now.getTime() - new Date(date).getTime();
                const diffMins = Math.floor(diffMs / 60000);
                const diffHours = Math.floor(diffMs / 3600000);
                const diffDays = Math.floor(diffMs / 86400000);
                
                if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
                if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
              };

              // Helper function to calculate total hours from timesheet entries
              const getTotalHours = (entries: { hours: { monday: number; tuesday: number; wednesday: number; thursday: number; friday: number; saturday: number; sunday: number } }[]) => {
                return entries.reduce((total, entry) => {
                  const h = entry.hours;
                  return total + h.monday + h.tuesday + h.wednesday + h.thursday + h.friday + h.saturday + h.sunday;
                }, 0);
              };

              // Demo data for fallback
              const demoActivities = [
                {
                  employee: 'Sarah Johnson',
                  action: 'Submitted timesheet',
                  description: 'Week ending 22/12/2024',
                  time: '30 minutes ago',
                  icon: Clock,
                  color: 'text-info-600',
                  bgColor: 'bg-info-50',
                  type: 'submitted' as const,
                },
                {
                  employee: 'Michael Chen',
                  action: 'Timesheet approved',
                  description: 'Week ending 15/12/2024 - 40 hours',
                  time: '2 hours ago',
                  icon: CheckSquare,
                  color: 'text-success-600',
                  bgColor: 'bg-success-50',
                  type: 'approved' as const,
                },
                {
                  employee: 'Emma Wilson',
                  action: 'Submitted timesheet',
                  description: 'Week ending 22/12/2024',
                  time: '3 hours ago',
                  icon: Clock,
                  color: 'text-info-600',
                  bgColor: 'bg-info-50',
                  type: 'submitted' as const,
                },
                {
                  employee: 'David Brown',
                  action: 'Overdue submission',
                  description: 'Week ending 15/12/2024 - 3 days overdue',
                  time: '3 days ago',
                  icon: AlertTriangle,
                  color: 'text-burntorange-600',
                  bgColor: 'bg-burntorange-50',
                  type: 'overdue' as const,
                },
              ];

              const getIconForType = (type: string) => {
                if (type === 'approved') return { icon: CheckSquare, color: 'text-success-600', bgColor: 'bg-success-50' };
                if (type === 'overdue') return { icon: AlertTriangle, color: 'text-burntorange-600', bgColor: 'bg-burntorange-50' };
                return { icon: Clock, color: 'text-info-600', bgColor: 'bg-info-50' };
              };

              // Use backend API data in live mode when available
              const apiActivities = mode === 'live' && managerSummary?.recentTeamActivity?.length
                ? managerSummary.recentTeamActivity.map(a => {
                    const style = getIconForType(a.type);
                    return {
                      employee: a.employeeName,
                      employeeId: a.employeeId,
                      action: a.action,
                      description: a.description,
                      time: getRelativeTime(a.activityDate ? new Date(a.activityDate) : undefined),
                      icon: style.icon,
                      color: style.color,
                      bgColor: style.bgColor,
                      type: a.type as 'submitted' | 'approved' | 'overdue',
                    };
                  })
                : null;

              // Build activities from timesheet context data as fallback
              const clientActivities = timesheets.length > 0
                ? timesheets
                    .filter(ts => ts.status === 'submitted' || ts.status === 'approved' || ts.status === 'draft')
                    .map(ts => {
                      const weekEndingDate = new Date(ts.weekEnding);
                      const weekEndingStr = format(weekEndingDate, 'dd/MM/yyyy');
                      const totalHours = getTotalHours(ts.entries);
                      const now = new Date();
                      const daysSinceWeekEnding = Math.floor((now.getTime() - weekEndingDate.getTime()) / 86400000);
                      
                      if (ts.status === 'approved') {
                        return {
                          employee: ts.employeeName,
                          action: 'Timesheet approved',
                          description: `Week ending ${weekEndingStr} - ${totalHours} hours`,
                          time: getRelativeTime(ts.approvedAt),
                          icon: CheckSquare,
                          color: 'text-success-600',
                          bgColor: 'bg-success-50',
                          type: 'approved' as const,
                          sortDate: ts.approvedAt ? new Date(ts.approvedAt) : weekEndingDate,
                        };
                      } else if (ts.status === 'submitted') {
                        return {
                          employee: ts.employeeName,
                          employeeId: ts.employeeId,
                          action: 'Submitted timesheet',
                          description: `Week ending ${weekEndingStr}`,
                          time: getRelativeTime(ts.submittedAt),
                          icon: Clock,
                          color: 'text-info-600',
                          bgColor: 'bg-info-50',
                          type: 'submitted' as const,
                          sortDate: ts.submittedAt ? new Date(ts.submittedAt) : weekEndingDate,
                        };
                      } else if (ts.status === 'draft' && daysSinceWeekEnding > 7) {
                        return {
                          employee: ts.employeeName,
                          action: 'Overdue submission',
                          description: `Week ending ${weekEndingStr} - ${daysSinceWeekEnding - 7} days overdue`,
                          time: `${daysSinceWeekEnding} days ago`,
                          icon: AlertTriangle,
                          color: 'text-burntorange-600',
                          bgColor: 'bg-burntorange-50',
                          type: 'overdue' as const,
                          sortDate: weekEndingDate,
                        };
                      }
                      return null;
                    })
                    .filter((activity): activity is NonNullable<typeof activity> => activity !== null)
                    .sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime())
                    .slice(0, 10)
                : null;

              const activities = apiActivities || clientActivities || demoActivities;

              return activities
                .filter(item => activityFilter === 'all' || item.type === activityFilter)
                .map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                    <div className={`p-2 rounded-lg ${item.bgColor}`}>
                      <item.icon className={`h-4 w-4 ${item.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{item.employee}</p>
                        <span className="text-sm text-muted-foreground">•</span>
                        <p className="text-sm text-muted-foreground truncate">{item.action}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <p className="text-xs text-muted-foreground whitespace-nowrap">{item.time}</p>
                      {item.type === 'submitted' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 w-7 p-0"
                          onClick={() => onNavigate?.('/approvals')}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ));
            })()}
          </div>
        </CardContent>
      </Card>
      </div> {/* End of grid for Team Performance and Recent Team Activity */}
      </div> {/* End of Team Details section */}

      {/* Bulk Approve Confirmation Dialog */}
      <AlertDialog open={showBulkApproveConfirm} onOpenChange={setShowBulkApproveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk Approval</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to approve {timesheetsToApprove.length} timesheet{timesheetsToApprove.length !== 1 ? 's' : ''} for {selectedMembers.size} team member{selectedMembers.size !== 1 ? 's' : ''}.
              This will mark them as approved with you ({currentEmployee.name}) recorded as the approver.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeBulkApprove}>
              Approve {timesheetsToApprove.length} Timesheet{timesheetsToApprove.length !== 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
